import { ChangeDetectionStrategy, Component, ElementRef, HostListener, Input, OnInit } from '@angular/core';
import {
  CmsSimpleResponsiveBannerComponent,
  CustomCmsBannerComponent,
  CustomCmsBannerComponentMedia,
  Media,
} from '@promo/const';
import { DeviceTypes } from '@shared/models';
import { DeviceService } from '@shared/services';
import { SdPhotoswipeImage, SdPhotoswipeService } from '@shared/services/photoswipe.service';
import { RouterState, RoutingService } from '@spartacus/core';
import { BREAKPOINT, PageSlotComponent } from '@spartacus/storefront';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Component({
  selector: 'sd-banner-content',
  templateUrl: './banner-content.component.html',
  styleUrls: ['./banner-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SdBannerContentComponent implements OnInit {
  @Input() responsiveBannerData?: CmsSimpleResponsiveBannerComponent;
  @Input() simpleBannerData?: CustomCmsBannerComponent;
  @Input() useGallery = false;
  media: Media;
  loadImageEagerly$: Observable<boolean>;

  constructor(
    private deviceService: DeviceService,
    private photoswipeService: SdPhotoswipeService,
    private elRef: ElementRef,
    private routingService: RoutingService,
    private pageSlotComponent: PageSlotComponent,
  ) {}

  ngOnInit(): void {
    // В целях оптимизации первые 5 баннеров на главной странице имеют приоритет загрузки
    this.loadImageEagerly$ = this.routingService.getRouterState().pipe(
      take(1),
      map((routerState: RouterState) => {
        const currentRoute = routerState.state.semanticRoute;
        const currentSlot = this.pageSlotComponent.position;
        const bannerUid = this.responsiveBannerData?.uid || this.simpleBannerData?.uid;

        if (currentRoute !== 'home' || currentSlot !== 'BannerSection' || !bannerUid) {
          return false;
        }

        return (
          this.pageSlotComponent.components
            .filter((b) => b.typeCode?.toLowerCase().includes('banner'))
            .findIndex((c) => c.uid === bannerUid) <= 4
        );
      }),
    );

    this.media = this.getMedia();
  }

  getHeightRatio(media: CustomCmsBannerComponentMedia): string | undefined {
    if (!media.height || !media.width) {
      return undefined;
    }

    return `${(Number(media.height) / Number(media.width)) * 100}%`;
  }

  @HostListener('click') onClick(): void {
    if (this.useGallery) {
      let media: CustomCmsBannerComponentMedia | undefined;
      if (this.responsiveBannerData && this.responsiveBannerData.media) {
        this.deviceService.deviceType$.pipe(take(1)).subscribe((deviceType) => {
          const responsiveMedia = this.responsiveBannerData?.media;
          if (!responsiveMedia) {
            return;
          }
          switch (deviceType) {
            case DeviceTypes.mobile:
              media = responsiveMedia.mobile;
              break;
            case DeviceTypes.tablet:
              media = responsiveMedia.tablet;
              break;
            default:
              media = responsiveMedia.widescreen || responsiveMedia.desktop;
          }
          this.openGallery(media);
        });
      } else {
        media = this.simpleBannerData?.media;
        this.openGallery(media);
      }
    }
  }

  private getMedia(): Media {
    return {
      mobile: `(min-width: 0px)`,
      tablet: `(min-width: ${this.deviceService.getSize(BREAKPOINT.sm)}px)`,
      desktop: `(min-width: ${this.deviceService.getSize(BREAKPOINT.md)}px)`,
      widescreen: `(min-width: ${this.deviceService.getSize(BREAKPOINT.lg)}px)`,
    };
  }

  private openGallery(media: CustomCmsBannerComponentMedia | undefined): void {
    if (!media) {
      return;
    }

    if (!media?.url) {
      return;
    }

    const image: SdPhotoswipeImage = {
      src: media.url,
      w: media.width ? parseInt(media.width, 10) : 0,
      h: media.height ? parseInt(media.height, 10) : 0,
      element: this.elRef.nativeElement as HTMLElement,
    };

    this.photoswipeService.openGallery(image).subscribe();
  }
}
